import { useState, useCallback, useMemo } from 'react';

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocalhost ? 'http://localhost:5000' : '';

interface VocabWord {
  word: string;
  context: string;
  pronunciation: string;
  output_pronunciation?: string;
  input_aliases?: string[];
  voice_replacement?: string;
}

interface BehaviorModifier {
  modifier_text: string;
  behavior_mode?: 'blueprint' | 'custom';
  effective_modifier_text?: string;
  updated_at: string | null;
  onboarding_memory_text?: string;
  onboarding_memory_updated_at?: string | null;
  setup_blueprint?: {
    revision?: number | null;
    behavior_text?: string;
    available?: boolean;
  };
}

interface OnboardingMemoryData {
  memory_text: string;
  updated_at: string | null;
}

interface AdminProfile {
  display_name: string;
  email?: string | null;
  updated_at: string | null;
}

type ManagedUserRole = 'user' | 'admin' | 'owner' | 'superadmin';
type ManagedUserEditableRole = 'user' | 'admin' | 'superadmin';

interface CustomerMembership {
  id?: string | null;
  user_id?: string | null;
  customer_id?: string | null;
  customer_slug?: string | null;
  customer_name?: string | null;
  role: 'customeradmin' | string;
  status: 'active' | 'disabled' | 'pending' | string;
  username?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface AdminSessionContext {
  username: string | null;
  user_id?: string | null;
  role: string | null;
  platform_role?: string | null;
  is_superadmin: boolean;
  has_active_customeradmin?: boolean;
  active_customer_id?: string | null;
  active_customer_slug?: string | null;
  active_customer_name?: string | null;
  active_workspace_slug?: string | null;
  active_workspace_name?: string | null;
  active_agent_slug?: string | null;
  active_agent_name?: string | null;
  customer_memberships?: CustomerMembership[];
  agent_access?: Array<{
    id?: string;
    customer_slug?: string;
    workspace_slug?: string;
    agent_slug?: string;
    status?: string;
  }>;
}

interface ManagedUser {
  id: string;
  username: string;
  email?: string | null;
  role: ManagedUserRole;
  status: 'pending' | 'active' | 'disabled' | 'password_reset_required';
  is_verified: boolean;
  must_change_password: boolean;
  created_at: string | null;
  updated_at: string | null;
  last_login_at: string | null;
  verified_at: string | null;
  created_by?: string | null;
  verified_by?: string | null;
}

interface SuperadminUserAgentAssignment {
  customer_id?: string | null;
  customer_slug?: string | null;
  customer_name?: string | null;
  workspace_id?: string | null;
  workspace_slug?: string | null;
  workspace_name?: string | null;
  agent_id?: string | null;
  agent_slug?: string | null;
  agent_name?: string | null;
  status?: string | null;
  membership_role?: string | null;
  membership_status?: string | null;
}

interface SuperadminUserRecord extends ManagedUser {
  memberships: CustomerMembership[];
  agents: SuperadminUserAgentAssignment[];
}

interface SuperadminUsersResponse {
  generated_at: string;
  users: SuperadminUserRecord[];
}

type AvatarProfileStatus = 'draft' | 'published' | 'archived';

interface AvatarAssignedCustomer {
  id: string;
  slug: string;
  name: string;
  status?: string | null;
}

interface AvatarProfile {
  id: string;
  slug: string;
  name: string;
  description: string;
  role_label: string;
  provider_avatar_id: string;
  portrait_url: string;
  system_prompt: string;
  behavior_prompt: string;
  status: AvatarProfileStatus;
  is_system: boolean;
  version: number;
  assigned_customers: AvatarAssignedCustomer[];
  created_at?: string | null;
  updated_at?: string | null;
  agent_id?: string | null;
  agent_slug?: string | null;
  agent_name?: string | null;
  workspace_slug?: string | null;
}

interface AvatarCustomerOption {
  id: string;
  slug: string;
  name: string;
  status?: string | null;
}

interface SuperadminAvatarsResponse {
  profiles: AvatarProfile[];
  customers: AvatarCustomerOption[];
  generated_at?: string | null;
}

interface AvatarLabSection {
  label: string;
  content: string;
  source: string;
  length: number;
  sha256: string;
}

interface AvatarLabSnapshot {
  scope: { customer_slug: string; workspace_slug: string; agent_slug: string };
  avatar: {
    id?: string | null;
    slug: string;
    agent_slug: string;
    name: string;
    role_label: string;
    description: string;
    portrait_url: string;
  };
  behavior: {
    mode: 'blueprint' | 'custom';
    source: 'blueprint' | 'custom';
    blueprint_revision?: number | null;
    custom_identical_to_blueprint: boolean;
  };
  lengths: Record<'core' | 'behavior' | 'text_prompt' | 'voice_prompt' | 'global_overlay', number>;
  fingerprints: Record<'core' | 'behavior' | 'text_prompt' | 'voice_prompt' | 'global_overlay', string>;
  knowledge: { documents: number; chunks: number; available: boolean };
  sections: AvatarLabSection[];
}

interface AvatarLabComparison {
  core_similarity: number | null;
  behavior_similarity: number | null;
  voice_similarity: number | null;
  identical_hashes: Record<string, boolean>;
  similarity_warning: boolean;
}

interface AvatarLabResponse {
  generated_at: string;
  left: AvatarLabSnapshot;
  right: AvatarLabSnapshot | null;
  comparison: AvatarLabComparison | null;
}

type LiveAvatarCatalogSource = 'user' | 'public' | 'both';

interface LiveAvatarCatalogEntry {
  id: string;
  name: string;
  type: string;
  status: string;
  preview_url: string;
  is_expired: boolean;
  is_1080p: boolean;
  default_voice: string;
  source: LiveAvatarCatalogSource;
  is_green_screen: boolean;
}

interface LiveAvatarCatalogResponse {
  avatars: LiveAvatarCatalogEntry[];
  generated_at: string | null;
  cached: boolean;
}

interface AvatarProfileInput {
  slug: string;
  name: string;
  description: string;
  role_label: string;
  provider_avatar_id: string;
  portrait_url: string;
  system_prompt: string;
  behavior_prompt: string;
  status: AvatarProfileStatus;
}

interface VocabularyData {
  words: VocabWord[];
  updated_at: string | null;
  vocabulary_scope?: {
    customer_slug: string;
    shared_across_agents: boolean;
    source?: string;
  };
}

interface PromptSection {
  label: string;
  content: string;
  editable: boolean;
  source?: string;
  advanced?: boolean;
  raw_content?: string;
  line_count?: number;
}

interface PromptDiagnostics {
  realtime_config: Record<string, string>;
  sections: PromptSection[];
}

interface PromptPreview {
  persona: string;
  sections: PromptSection[];
  total_length: number;
  total_lines?: number;
  diagnostics?: PromptDiagnostics;
}

interface CallToActionsData {
  content: string;
  count: number;
  path: string;
  line_count?: number;
  updated_at?: string;
  scope?: {
    customer_slug?: string | null;
    workspace_slug?: string | null;
    agent_slug?: string | null;
  };
}

interface UsageBucket {
  customer_slug?: string;
  workspace_slug?: string;
  agent_slug?: string;
  user_id?: string | null;
  username?: string | null;
  display_name?: string | null;
  is_anonymous?: boolean;
  week_start?: string | null;
  week_end?: string | null;
  week_label?: string | null;
  day?: string | null;
  conversation_count: number;
  message_count: number;
  text_conversation_count: number;
  voice_conversation_count: number;
  unknown_mode_conversation_count: number;
  last_conversation_at: string | null;
  usage_event_count: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  realtime_duration_seconds: number;
  realtime_minutes: number;
  estimated_cost_usd: number;
  estimated_openai_cost_usd: number;
  estimated_openai_cost_eur: number;
  liveavatar_minutes: number;
  liveavatar_credits: number;
  liveavatar_observed_credits?: number;
  liveavatar_inferred_credits?: number;
  liveavatar_event_count: number;
  liveavatar_token_count: number;
  liveavatar_session_start_count: number;
  liveavatar_session_end_count: number;
  liveavatar_reconnect_count: number;
  liveavatar_disconnect_count: number;
  liveavatar_timeout_count: number;
  liveavatar_start_failure_count: number;
  liveavatar_prewarm_count: number;
  liveavatar_keep_alive_count: number;
  liveavatar_audio_chunk_count: number;
  liveavatar_audio_bytes: number;
  estimated_liveavatar_cost_eur: number;
  estimated_total_cost_eur: number;
  priced_event_count: number;
  unpriced_event_count: number;
  model_counts: Record<string, number>;
  last_usage_event_at: string | null;
  last_usage_at: string | null;
}

interface UsagePricingConfig {
  pricing_version?: string;
  currency?: string;
  model_price_source?: string;
  usd_to_eur_rate?: number;
  usd_to_eur_source?: string;
  liveavatar?: {
    mode?: string;
    eur_per_credit?: number;
    credits_per_minute?: number;
    source?: string;
    billing_model?: string;
    monthly_fee_eur?: number;
    included_credits?: number;
    allocated_value_per_credit_eur?: number;
    overage_eur_per_credit?: number | null;
    plan_source?: string;
    usage_source?: string;
  };
  models?: Record<string, Record<string, number | string>>;
}

interface SuperadminBillingPeriod {
  start?: string | null;
  end?: string | null;
  period_start?: string | null;
  period_end?: string | null;
  timezone?: string | null;
  label?: string | null;
}

interface SuperadminBillingSummary {
  billing_model?: string;
  monthly_fee_eur?: number;
  fixed_cost_eur?: number;
  included_credits?: number;
  consumed_credits?: number;
  total_internal_credits?: number;
  remaining_credits?: number;
  utilization_percent?: number;
  utilization_pct?: number;
  openai_variable_cost_eur?: number;
  allocated_usage_value_eur?: number;
  estimated_overage_eur?: number | null;
  total_committed_plus_variable_eur?: number;
  forecast_exhausted_at?: string | null;
  forecast_exhaustion_at?: string | null;
  projected_cycle_credits?: number;
  average_credits_per_day?: number;
  excess_credits?: number;
  overage_pricing_configured?: boolean;
  observed_credits?: number;
  inferred_credits?: number;
  provider_credits?: number | null;
  unpriced_event_count?: number;
}

interface SuperadminCostAlert {
  id?: string;
  key?: string;
  severity?: 'critical' | 'warning' | 'info' | 'ok' | string;
  title?: string;
  message?: string;
  reason?: string;
  threshold?: number | null;
  threshold_value?: number | null;
  current_value?: number | null;
  value?: number | null;
  unit?: string | null;
  created_at?: string | null;
  triggered_at?: string | null;
  detected_at?: string | null;
  recommended_action?: string | null;
  status?: string | null;
}

interface SuperadminReconciliation {
  status?: string;
  last_synced_at?: string | null;
  provider_credits?: number | null;
  internal_credits?: number | null;
  difference_credits?: number | null;
  provider_credits_left?: number | null;
  matched_session_count?: number;
  provider_only_count?: number;
  internal_only_count?: number;
  message?: string | null;
  note?: string | null;
}

interface SuperadminPricingCoverage {
  priced_event_count?: number;
  unpriced_event_count?: number;
  coverage_percent?: number;
  coverage_pct?: number;
  source?: string | null;
}

interface WorkspaceOverview {
  id?: string | null;
  customer_id?: string | null;
  slug: string;
  name: string;
  status: string;
  agent_count: number;
  agents: Array<{
    id?: string | null;
    customer_id?: string | null;
    workspace_id?: string | null;
    slug: string;
    name: string;
    status: string;
    created_at?: string | null;
    updated_at?: string | null;
  }>;
  created_at?: string | null;
  updated_at?: string | null;
}

interface CustomerOverview {
  id?: string | null;
  slug: string;
  name: string;
  status: string;
  workspace_count: number;
  agent_count: number;
  membership_count: number;
  active_customeradmin_count: number;
  usage: UsageBucket;
  agent_usage: UsageBucket[];
  user_usage?: UsageBucket[];
  anonymous_usage?: UsageBucket;
  workspaces: WorkspaceOverview[];
  memberships: CustomerMembership[];
  created_at?: string | null;
  updated_at?: string | null;
}

interface SuperadminOverview {
  generated_at: string;
  usage_mode: string;
  usage_note: string;
  pricing: UsagePricingConfig;
  totals: UsageBucket & {
    customer_count: number;
    workspace_count: number;
    agent_count: number;
    user_count: number;
    membership_count: number;
    active_customeradmin_count: number;
    role_counts: Record<string, number>;
  };
  customers: CustomerOverview[];
  usage_by_customer: UsageBucket[];
  usage_by_agent: UsageBucket[];
  usage_by_user: UsageBucket[];
  anonymous_usage: UsageBucket;
  anonymous_by_customer: UsageBucket[];
  weekly_usage: UsageBucket[];
  daily_usage?: UsageBucket[];
  billing_period?: SuperadminBillingPeriod;
  billing_summary?: SuperadminBillingSummary;
  alerts?: SuperadminCostAlert[];
  reconciliation?: SuperadminReconciliation;
  pricing_coverage?: SuperadminPricingCoverage;
  liveavatar_monitoring?: {
    tracked_lifecycle_count: number;
    active_lifecycle_count: number;
    completed_lifecycle_count: number;
    stale_lifecycle_count: number;
    token_without_start_count: number;
    timed_out_lifecycle_count: number;
    reconnect_limited_count: number;
    start_failure_count: number;
    safety?: {
      max_tokens_per_client_10m: number;
      max_tokens_per_hour: number;
      max_tokens_per_day: number;
      max_concurrent_sessions: number;
      daily_credit_budget: number;
      monthly_credit_budget?: number;
      effective_credits_billing_period?: number;
      billing_period_start_utc?: string | null;
      reserved_credits_per_token: number;
      session_reservation_seconds: number;
      tokens_last_hour: number;
      tokens_today: number;
      active_reservations: number;
      reported_credits_today: number;
      reserved_credits: number;
      blocked_tokens_24h?: number;
      storage_scope: string;
    };
    recent_sessions: Array<{
      lifecycle_id: string;
      provider_session_ids: string[];
      customer_slug?: string;
      workspace_slug?: string;
      agent_slug?: string;
      chat_id?: string | null;
      entrypoint?: string | null;
      client_fingerprint?: string | null;
      user_agent?: string | null;
      status: string;
      first_event_at?: string | null;
      last_event_at?: string | null;
      token_count: number;
      session_start_count: number;
      session_end_count: number;
      reconnect_count: number;
      disconnect_count: number;
      timeout_count: number;
      start_failure_count: number;
      keep_alive_count: number;
      audio_chunk_count: number;
      audio_bytes: number;
      duration_seconds: number;
      credits: number;
      last_stop_reason?: string | null;
    }>;
  };
}

interface KnowledgeSummaryMetadata {
  generated_at?: string | null;
  source_hash?: string | null;
  document_count?: number;
  chunk_count?: number;
  selected_chunk_count?: number;
  source_char_count?: number;
}

interface KnowledgeSummaryData extends KnowledgeSummaryMetadata {
  summary_text: string;
  updated_at: string | null;
  updated_by?: string | null;
  char_count?: number;
  line_count?: number;
  base_text?: string;
  metadata?: KnowledgeSummaryMetadata;
}

interface AdminOnboardingStartResponse {
  agent_name: string;
  company_name: string;
  admin_name: string;
  initial_message: string;
  questions: string[];
  current_question_index: number;
  question_states: AdminOnboardingQuestionState[];
}

interface AdminOnboardingQuestionState {
  question: string;
  status: 'pending' | 'asked' | 'answered';
  summary: string;
}

interface AdminOnboardingMessage {
  role: 'user' | 'assistant';
  content: string;
  id?: string;
  memory?: FeedbackChatModifier;
}

interface AdminOnboardingMessageResponse {
  assistant_message: string;
  memory_draft?: FeedbackChatModifier | null;
  session_mode: 'questions' | 'free';
  current_question_index: number;
  asked_question_index: number | null;
  question_states: AdminOnboardingQuestionState[];
  question_status?: 'still_open' | 'answered' | 'free';
}

interface AdminOnboardingMemoryApplyResponse {
  memory_text: string;
  updated_at: string;
  entry_count: number;
  scope?: string;
  workspace_slug?: string | null;
}

interface DocumentInfo {
  document_name: string;
  chunk_count: number;
  total_chars: number;
}

interface DocumentListResponse {
  documents: DocumentInfo[];
  total_documents: number;
  total_chunks: number;
}

interface ChunkInfo {
  chunk_id: string;
  chunk_index: number;
  total_chunks: number;
  text_preview: string;
  chunk_chars: number;
  chunk_tokens: number;
}

interface ChunkListResponse {
  document_name: string;
  chunks: ChunkInfo[];
  chunk_count: number;
}

interface ChunkDetail {
  chunk_id: string;
  document_name: string;
  text: string;
  metadata: Record<string, any>;
}

type DeepScrapeRunStatus = 'queued' | 'running' | 'succeeded' | 'partial' | 'failed' | 'cancelled';

interface DeepScrapeConfig {
  max_pages: number;
  max_depth: number;
  max_duration_seconds: number;
  include_pdfs: boolean;
  allowed_subdomains: string[];
  include_patterns: string[];
  exclude_patterns: string[];
  request_delay_seconds: number;
}

interface DeepScrapeSource {
  id: string;
  root_url: string;
  normalized_root_url: string;
  active_run_id?: string | null;
  status: 'active' | 'disabled';
  config: DeepScrapeConfig;
  created_at: string;
  updated_at: string;
}

interface DeepScrapeRun {
  id: string;
  source_id: string;
  root_url?: string;
  normalized_root_url?: string;
  active_run_id?: string | null;
  status: DeepScrapeRunStatus;
  config: DeepScrapeConfig;
  pages_discovered: number;
  pages_fetched: number;
  pages_indexed: number;
  pages_skipped: number;
  error_count: number;
  chunks_created: number;
  bytes_fetched: number;
  cancel_requested: boolean;
  error_message?: string | null;
  report?: Record<string, any>;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
}

interface DeepScrapeListResponse {
  sources: DeepScrapeSource[];
  runs: DeepScrapeRun[];
}

interface DeepScrapeInput {
  url: string;
  max_pages: number;
  max_depth: number;
  max_duration_seconds?: number;
  include_pdfs: boolean;
  allowed_subdomains?: string[];
  include_patterns?: string[];
  exclude_patterns?: string[];
}

interface ConversationSummary {
  chat_id: string;
  message_count: number;
  last_modified: string;
  created_at: string;
  preview: string;
  match_snippet?: string | null;
  has_feedback?: boolean;
  customer_slug?: string | null;
  workspace_slug?: string | null;
  agent_slug?: string | null;
  user_id?: string | null;
  username?: string | null;
  user_agent?: string | null;
}

interface DebugAudioMeta {
  audio_id: string;
  duration_ms: number;
  sample_rate: number;
  format: string;
  storage_key: string;
  byte_length?: number;
  source?: 'user' | 'assistant' | string;
  voice?: string;
}

interface DebugConversationMessage {
  role: 'user' | 'assistant' | 'link';
  content: string;
  actions?: Array<Record<string, any>>;
  debug_audio?: DebugAudioMeta | DebugAudioMeta[];
  debug_audio_segments?: DebugAudioMeta[];
}

interface DebugConversationSummary {
  chat_id: string;
  message_count: number;
  debug_audio_count: number;
  last_modified: string;
  created_at: string;
  preview: string;
  workspace_slug?: string | null;
  username?: string | null;
  user_agent?: string | null;
  debug_mode: boolean;
  debug_metadata?: Record<string, any> | null;
}

interface DebugConversationDetail {
  chat_id: string;
  messages: DebugConversationMessage[];
  created_at: string;
  last_modified: string;
  workspace_slug?: string | null;
  username?: string | null;
  user_agent?: string | null;
  origin_mode?: string | null;
  debug_mode: boolean;
  debug_metadata?: Record<string, any> | null;
}

interface DebugProfileSettings {
  gate?: Record<string, any>;
  playback?: Record<string, any>;
  avatarPacing?: Record<string, any>;
  transcriptTuning?: Record<string, any>;
  pronunciation?: {
    voice?: string;
    replacement_overrides?: Array<{
      word: string;
      voice_replacement: string;
      note?: string;
    }>;
    prompt_notes?: string;
    phrase_tests?: Array<{
      id?: string;
      phrase: string;
      expected?: string;
      note?: string;
    }>;
  };
  muteDuringSpeaking?: boolean;
}

interface DebugProfile {
  id: string;
  name: string;
  description?: string;
  status?: string;
  target?: 'transcription' | 'pronunciation' | 'balanced' | string;
  tags?: string[];
  settings: DebugProfileSettings;
  based_on_profile_id?: string | null;
  source_chat_ids?: string[];
  locked?: boolean;
  factory?: boolean;
  created_by?: string;
  created_at?: string;
  updated_by?: string;
  updated_at?: string;
}

interface DebugProfilesResponse {
  factory_profile_id: string;
  profiles: DebugProfile[];
  updated_at?: string | null;
}

interface DebugEvaluation {
  id?: string;
  chat_id: string;
  message_index: number;
  role?: string;
  audio_id?: string;
  aspect: 'transcription' | 'pronunciation' | string;
  profile_id?: string | null;
  rating?: number;
  issue_type?: string;
  selected_text?: string;
  expected_text?: string;
  note?: string;
  created_by?: string;
  created_at?: string;
  updated_by?: string;
  updated_at?: string;
}

interface FeedbackComment {
  messageIndex: number;
  selectedText: string;
  commentText: string;
}

interface FeedbackData {
  comments: Array<{
    id: string;
    messageIndex: number;
    selectedText: string;
    commentText: string;
    timestamp: string;
  }>;
  last_feedback_at: string | null;
  applied_at?: string | null;
  applied_instruction?: string | null;
}

interface FeedbackGenerateResponse {
  suggested_text: string;
  reasoning_summary: string | null;
  comment_count: number;
  conversation_message_count: number;
  chat_id: string;
}

interface FeedbackApplyResponse {
  modifier_text: string;
  updated_at: string;
  entry_count: number;
  scope?: string;
  workspace_slug?: string | null;
}

interface FeedbackReference {
  id?: string;
  sourceMessageIndex: number;
  sourceRole: 'user' | 'assistant' | 'mixed';
  selectedText: string;
  sourceContent?: string;
}

interface FeedbackChatModifier {
  text: string;
  original_text?: string;
  status: 'draft' | 'accepted';
  accepted_at?: string | null;
  edited_at?: string | null;
  applied_scope?: string | null;
  applied_workspace_slug?: string | null;
}

interface FeedbackChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  references?: FeedbackReference[];
  modifier?: FeedbackChatModifier;
}

interface FeedbackChatData {
  started_at?: string | null;
  messages: FeedbackChatMessage[];
}

interface FeedbackChatSendResponse {
  assistant_message: FeedbackChatMessage | null;
  feedback_chat: FeedbackChatData;
  debug_consumed_action_logs?: string[];
  skipped_modifier_generation?: boolean;
}

interface FeedbackChatModifierUpdateResponse {
  message: FeedbackChatMessage;
  feedback_chat: FeedbackChatData;
  debug_pending_action_logs?: string[];
}

export function useAdminApi() {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem('admin_token'));
  const [username, setUsername] = useState<string | null>(() => sessionStorage.getItem('admin_username'));
  const [adminSession, setAdminSession] = useState<AdminSessionContext | null>(() => {
    try {
      const raw = sessionStorage.getItem('admin_session');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!token;

  const authHeaders = useCallback(() => {
    const currentToken = sessionStorage.getItem('admin_token') || token;
    return {
      'Content-Type': 'application/json',
      ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
    };
  }, [token]);

  const persistAdminSession = useCallback((session: AdminSessionContext | null) => {
    setAdminSession(session);
    if (session) {
      sessionStorage.setItem('admin_session', JSON.stringify(session));
    } else {
      sessionStorage.removeItem('admin_session');
    }
  }, []);

  const sessionFromAuthPayload = useCallback((data: any): AdminSessionContext => ({
    username: data.username || null,
    user_id: data.user?.id || data.user_id || null,
    role: data.role || null,
    platform_role: data.platform_role || null,
    is_superadmin: data.role === 'superadmin' || data.platform_role === 'superadmin' || data.platform_role === 'owner',
    has_active_customeradmin: Boolean(data.has_active_customeradmin || data.customer_memberships?.length),
    active_customer_id: data.active_customer_id || data.customer?.id || null,
    active_customer_slug: data.active_customer_slug || data.customer?.slug || null,
    active_customer_name: data.active_customer_name || data.customer?.name || data.customer_memberships?.[0]?.customer_name || null,
    active_workspace_slug: data.active_workspace_slug || data.workspace?.slug || null,
    active_workspace_name: data.active_workspace_name || data.workspace?.name || null,
    active_agent_slug: data.active_agent_slug || data.agent?.slug || null,
    active_agent_name: data.active_agent_name || data.agent?.name || null,
    customer_memberships: data.customer_memberships || [],
    agent_access: data.agent_access || [],
  }), []);

  const login = useCallback(async (user: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: user, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Login failed');
        return false;
      }
      setToken(data.token);
      setUsername(data.username);
      sessionStorage.setItem('admin_token', data.token);
      sessionStorage.setItem('admin_username', data.username);
      persistAdminSession(sessionFromAuthPayload(data));
      return true;
    } catch (e: any) {
      setError(e.message || 'Network error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [persistAdminSession, sessionFromAuthPayload]);

  const logout = useCallback(() => {
    setToken(null);
    setUsername(null);
    persistAdminSession(null);
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_username');
  }, [persistAdminSession]);

  const getAdminContext = useCallback(async (): Promise<AdminSessionContext | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/context`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load admin context');
      const nextSession: AdminSessionContext = {
        username: data.username || username,
        user_id: data.user_id || null,
        role: data.role || null,
        platform_role: data.platform_role || null,
        is_superadmin: Boolean(data.is_superadmin),
        has_active_customeradmin: Boolean(data.has_active_customeradmin),
        active_customer_id: data.customer?.id || data.scope?.customer_id || null,
        active_customer_slug: data.scope?.customer_slug || data.customer?.slug || null,
        active_customer_name: data.customer?.name || data.scope?.customer_slug || null,
        active_workspace_slug: data.scope?.workspace_slug || data.workspace?.slug || null,
        active_workspace_name: data.workspace?.name || data.scope?.workspace_slug || null,
        active_agent_slug: data.scope?.agent_slug || data.agent?.slug || null,
        active_agent_name: data.agent?.name || data.scope?.agent_slug || null,
        customer_memberships: data.customer_memberships || [],
        agent_access: data.agent_access || [],
      };
      persistAdminSession(nextSession);
      return nextSession;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout, persistAdminSession, username]);

  const switchAdminScope = useCallback(async (
    customerSlug: string,
    agentSlug = 'sally',
    workspaceSlug = 'main',
  ): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/scope`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          customerSlug,
          workspaceSlug,
          agentSlug,
        }),
      });
      const data = await res.json().catch(() => null);
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) throw new Error(data?.error || 'Failed to switch admin scope');
      setToken(data.token);
      setUsername(data.username);
      sessionStorage.setItem('admin_token', data.token);
      sessionStorage.setItem('admin_username', data.username);
      persistAdminSession(sessionFromAuthPayload(data));
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  }, [authHeaders, logout, persistAdminSession, sessionFromAuthPayload]);

  const getBehavior = useCallback(async (): Promise<BehaviorModifier | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/behavior`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load behavior');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getAdminProfile = useCallback(async (): Promise<AdminProfile | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/profile`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load admin profile');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveAdminProfile = useCallback(async (displayName: string, email?: string | null): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/profile`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ display_name: displayName, email: email ?? null }),
      });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) throw new Error('Failed to save admin profile');
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const saveBehavior = useCallback(async (
    text: string,
    behaviorMode: 'blueprint' | 'custom' = 'custom',
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/behavior`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ modifier_text: text, behavior_mode: behaviorMode }),
      });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) throw new Error('Failed to save behavior');
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const getVocabulary = useCallback(async (): Promise<VocabularyData | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/vocabulary`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load vocabulary');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveVocabulary = useCallback(async (
    words: VocabWord[],
    expectedUpdatedAt?: string | null,
  ): Promise<VocabularyData | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/vocabulary`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ words, expected_updated_at: expectedUpdatedAt }),
      });
      if (res.status === 401) { logout(); return null; }
      if (res.status === 409) {
        throw new Error('Das gemeinsame Vocabulary wurde in einer anderen Sitzung geändert. Die aktuelle Version wurde neu geladen.');
      }
      if (!res.ok) throw new Error('Failed to save vocabulary');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const getPromptPreview = useCallback(async (extended = false): Promise<PromptPreview | null> => {
    setError(null);
    try {
      const url = `${API_BASE}/api/admin/prompt-preview${extended ? '?extended=true' : ''}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load prompt preview');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveCorePrompt = useCallback(async (persona: string, content: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/core-prompt`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ persona, content }),
      });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save core prompt');
      }
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const getCallToActions = useCallback(async (): Promise<CallToActionsData | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/call-to-actions`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load call to actions');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveCallToActions = useCallback(async (content: string): Promise<CallToActionsData | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/call-to-actions`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ content }),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save call to actions');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  // --- Knowledge Base ---

  const getKnowledgeSummary = useCallback(async (): Promise<KnowledgeSummaryData | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/knowledge-summary`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load knowledge summary');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveKnowledgeSummary = useCallback(async (
    summaryText: string,
    metadata?: KnowledgeSummaryMetadata
  ): Promise<KnowledgeSummaryData | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/knowledge-summary`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ summary_text: summaryText, metadata }),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save knowledge summary');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const generateKnowledgeSummary = useCallback(async (): Promise<KnowledgeSummaryData | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/knowledge-summary/generate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to generate knowledge summary');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const getDocuments = useCallback(async (): Promise<DocumentListResponse | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/documents`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load documents');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getDocumentChunks = useCallback(async (docName: string): Promise<ChunkListResponse | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/documents/${encodeURIComponent(docName)}/chunks`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load chunks');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getChunkDetail = useCallback(async (chunkId: string): Promise<ChunkDetail | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/chunks/${chunkId}`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Chunk not found');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const deleteChunk = useCallback(async (chunkId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const deleteRequest = (confirmed = false) => fetch(`${API_BASE}/api/admin/chunks/${chunkId}`, {
        method: 'DELETE',
        headers: {
          ...authHeaders(),
          ...(confirmed ? { 'X-Confirm-Remote-S3-Delete': 'true' } : {}),
        },
      });

      let res = await deleteRequest();
      if (res.status === 401) { logout(); return false; }
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        if (data.confirmation_required) {
          const confirmed = window.confirm(data.message || 'This will delete remote S3 knowledgebase data. Continue?');
          if (!confirmed) return false;
          res = await deleteRequest(true);
          if (res.status === 401) { logout(); return false; }
        }
      }
      if (!res.ok) throw new Error('Failed to delete chunk');
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const deleteDocument = useCallback(async (docName: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const deleteRequest = (confirmed = false) => fetch(`${API_BASE}/api/admin/documents/${encodeURIComponent(docName)}`, {
        method: 'DELETE',
        headers: {
          ...authHeaders(),
          ...(confirmed ? { 'X-Confirm-Remote-S3-Delete': 'true' } : {}),
        },
      });

      let res = await deleteRequest();
      if (res.status === 401) { logout(); return false; }
      if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        if (data.confirmation_required) {
          const confirmed = window.confirm(data.message || 'This will delete remote S3 knowledgebase data. Continue?');
          if (!confirmed) return false;
          res = await deleteRequest(true);
          if (res.status === 401) { logout(); return false; }
        }
      }
      if (!res.ok) throw new Error('Failed to delete document');
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const uploadDocument = useCallback(async (
    file: File,
    onProgress?: (status: string) => void
  ): Promise<{ document_name: string; chunks_created: number } | null> => {
    setError(null);
    onProgress?.('Uploading and ingesting...');
    const formData = new FormData();
    formData.append('file', file);
    try {
      // Note: do NOT set Content-Type — browser sets multipart boundary automatically
      const headers = authHeaders();
      delete (headers as any)['Content-Type'];
      const res = await fetch(`${API_BASE}/api/admin/documents/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Upload failed');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getDeepScrapes = useCallback(async (): Promise<DeepScrapeListResponse | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/deepscrape`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Deepscrapes konnten nicht geladen werden');
      return {
        sources: Array.isArray(data?.sources) ? data.sources : [],
        runs: Array.isArray(data?.runs) ? data.runs : [],
      };
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const startDeepScrape = useCallback(async (input: DeepScrapeInput): Promise<DeepScrapeRun | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/deepscrape`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(input),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Deepscrape konnte nicht gestartet werden');
      return data.run as DeepScrapeRun;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getDeepScrapeRun = useCallback(async (runId: string): Promise<DeepScrapeRun | null> => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/deepscrape/runs/${encodeURIComponent(runId)}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Deepscrape-Status konnte nicht geladen werden');
      return data.run as DeepScrapeRun;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const cancelDeepScrape = useCallback(async (runId: string): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/deepscrape/runs/${encodeURIComponent(runId)}/cancel`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return false; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Deepscrape konnte nicht abgebrochen werden');
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  }, [authHeaders, logout]);

  const activateDeepScrapeRun = useCallback(async (runId: string): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/deepscrape/runs/${encodeURIComponent(runId)}/activate`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return false; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Deepscrape-Version konnte nicht aktiviert werden');
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  }, [authHeaders, logout]);

  // --- Conversations ---

  const getConversations = useCallback(async (query?: string): Promise<ConversationSummary[] | null> => {
    setError(null);
    try {
      const url = query && query.trim()
        ? `${API_BASE}/api/conversations?q=${encodeURIComponent(query.trim())}`
        : `${API_BASE}/api/conversations`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Failed to load conversations');
      return data.conversations || [];
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getDebugConversations = useCallback(async (): Promise<DebugConversationSummary[] | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/debug/conversations`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load debug conversations');
      const data = await res.json();
      return data.conversations || [];
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getDebugConversation = useCallback(async (chatId: string): Promise<DebugConversationDetail | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/debug/conversations/${encodeURIComponent(chatId)}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load debug conversation');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const deleteDebugConversation = useCallback(async (chatId: string): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/debug/conversations/${encodeURIComponent(chatId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete debug conversation');
      }
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  }, [authHeaders, logout]);

  const getDebugAudioUrl = useCallback(async (chatId: string, audioId: string): Promise<string | null> => {
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/debug/audio/${encodeURIComponent(chatId)}/${encodeURIComponent(audioId)}`,
        { headers: authHeaders() },
      );
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load debug audio');
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getDebugProfiles = useCallback(async (): Promise<DebugProfilesResponse | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/debug/profiles`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load debug profiles');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveDebugProfile = useCallback(async (profile: Partial<DebugProfile>): Promise<DebugProfile | null> => {
    setError(null);
    try {
      const method = profile.id ? 'PATCH' : 'POST';
      const url = profile.id
        ? `${API_BASE}/api/admin/debug/profiles/${encodeURIComponent(profile.id)}`
        : `${API_BASE}/api/admin/debug/profiles`;
      const res = await fetch(url, {
        method,
        headers: authHeaders(),
        body: JSON.stringify({ profile }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save debug profile');
      return data.profile || null;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const cloneDebugProfile = useCallback(async (profileId: string, name?: string): Promise<DebugProfile | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/debug/profiles/${encodeURIComponent(profileId)}/clone`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to clone debug profile');
      return data.profile || null;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const deleteDebugProfile = useCallback(async (profileId: string): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/debug/profiles/${encodeURIComponent(profileId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete debug profile');
      }
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  }, [authHeaders, logout]);

  const getDebugEvaluations = useCallback(async (chatId?: string): Promise<DebugEvaluation[] | null> => {
    setError(null);
    try {
      const url = chatId
        ? `${API_BASE}/api/admin/debug/evaluations?chat_id=${encodeURIComponent(chatId)}`
        : `${API_BASE}/api/admin/debug/evaluations`;
      const res = await fetch(url, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load debug evaluations');
      const data = await res.json();
      return data.evaluations || [];
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveDebugEvaluation = useCallback(async (evaluation: DebugEvaluation): Promise<DebugEvaluation | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/debug/evaluations`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ evaluation }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save debug evaluation');
      return data.evaluation || null;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const deleteConversation = useCallback(async (chatId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${encodeURIComponent(chatId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete conversation');
      }
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  // --- Feedback → Behavior Refinement ---

  const generateFeedback = useCallback(async (
    chatId: string, comments: FeedbackComment[]
  ): Promise<FeedbackGenerateResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/feedback/generate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ chat_id: chatId, comments }),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to generate feedback');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const loadFeedback = useCallback(async (chatId: string): Promise<FeedbackData | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${chatId}/feedback`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) return null;
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveFeedback = useCallback(async (
    chatId: string,
    comments: Array<{ id: string; messageIndex: number; selectedText: string; commentText: string; timestamp: string }>
  ): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${chatId}/feedback`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ comments }),
      });
      if (res.status === 401) { logout(); return false; }
      return res.ok;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  }, [authHeaders, logout]);

  const applyFeedback = useCallback(async (
    instructionText: string, sourceChatId: string
  ): Promise<FeedbackApplyResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/feedback/apply`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ instruction_text: instructionText, source_chat_id: sourceChatId }),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to apply feedback');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const getOnboardingMemory = useCallback(async (): Promise<OnboardingMemoryData | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding-memory`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to load onboarding memory');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const saveOnboardingMemory = useCallback(async (text: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding-memory`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ memory_text: text }),
      });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) throw new Error('Failed to save onboarding memory');
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const startAdminOnboarding = useCallback(async (): Promise<AdminOnboardingStartResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/start`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) throw new Error('Failed to start admin onboarding');
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const sendAdminOnboardingMessage = useCallback(async (
    messages: AdminOnboardingMessage[],
    questions: string[],
    companyName: string,
    agentName: string,
    adminName: string,
    sessionMode: 'questions' | 'free',
    currentQuestionIndex: number,
    questionStates: AdminOnboardingQuestionState[],
    allowMemoryGeneration: boolean,
    pendingActionLogs: string[] = [],
  ): Promise<AdminOnboardingMessageResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/message`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          messages,
          questions,
          company_name: companyName,
          agent_name: agentName,
          admin_name: adminName,
          session_mode: sessionMode,
          current_question_index: currentQuestionIndex,
          question_states: questionStates,
          allow_memory_generation: allowMemoryGeneration,
          pending_action_logs: pendingActionLogs,
        }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send admin onboarding message');
      return data;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const applyAdminOnboardingMemory = useCallback(async (
    instructionText: string,
    sourceMessageId: string,
  ): Promise<AdminOnboardingMemoryApplyResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/onboarding/memory/apply`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          instruction_text: instructionText,
          source_message_id: sourceMessageId,
        }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to apply onboarding memory');
      return data;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const getUsers = useCallback(async (): Promise<ManagedUser[] | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Users konnten nicht geladen werden');
      return data.users || [];
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getSuperadminOverview = useCallback(async (): Promise<SuperadminOverview | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/overview`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load superadmin overview');
      return data;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getSuperadminUsers = useCallback(async (): Promise<SuperadminUsersResponse | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/users`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load superadmin users');
      return data;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getSuperadminAvatars = useCallback(async (): Promise<SuperadminAvatarsResponse | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/avatars`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Avatare konnten nicht geladen werden');
      return {
        profiles: Array.isArray(data?.profiles) ? data.profiles : (Array.isArray(data?.avatars) ? data.avatars : []),
        customers: Array.isArray(data?.customers) ? data.customers : [],
        generated_at: data?.generated_at || null,
      };
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getSuperadminAvatarLab = useCallback(async (
    customerSlug: string,
    workspaceSlug: string,
    agentSlug: string,
    compareAgentSlug?: string,
  ): Promise<AvatarLabResponse | null> => {
    setError(null);
    try {
      const query = new URLSearchParams({
        customer_slug: customerSlug,
        workspace_slug: workspaceSlug,
        agent_slug: agentSlug,
      });
      if (compareAgentSlug) query.set('compare_agent_slug', compareAgentSlug);
      const res = await fetch(`${API_BASE}/api/superadmin/avatar-lab?${query.toString()}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Avatar-Lab-Diagnose konnte nicht geladen werden');
      return data as AvatarLabResponse;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getLiveAvatarCatalog = useCallback(async (
    forceRefresh = false,
  ): Promise<LiveAvatarCatalogResponse | null> => {
    setError(null);
    try {
      const query = forceRefresh ? '?refresh=1' : '';
      const res = await fetch(`${API_BASE}/api/superadmin/liveavatar/avatars${query}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'LiveAvatar-Katalog konnte nicht geladen werden');
      return {
        avatars: Array.isArray(data?.avatars) ? data.avatars : [],
        generated_at: typeof data?.generated_at === 'string' ? data.generated_at : null,
        cached: Boolean(data?.cached),
      };
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const createAvatarProfile = useCallback(async (input: AvatarProfileInput): Promise<AvatarProfile | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/avatars`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(input),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Avatar konnte nicht erstellt werden');
      return (data?.profile || data?.avatar || data) as AvatarProfile;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const updateAvatarProfile = useCallback(async (
    profileId: string,
    input: AvatarProfileInput,
  ): Promise<AvatarProfile | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/avatars/${encodeURIComponent(profileId)}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(input),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Avatar konnte nicht gespeichert werden');
      return (data?.profile || data?.avatar || data) as AvatarProfile;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const setAvatarCustomers = useCallback(async (
    profileId: string,
    customerIds: string[],
  ): Promise<AvatarProfile | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/avatars/${encodeURIComponent(profileId)}/customers`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ customer_ids: customerIds }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Kundenfreigaben konnten nicht gespeichert werden');
      return (data?.profile || data?.avatar || data) as AvatarProfile;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const getCustomerAvatars = useCallback(async (customerSlug: string): Promise<AvatarProfile[] | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/customer/agents/${encodeURIComponent(customerSlug)}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Verfügbare Avatare konnten nicht geladen werden');
      if (Array.isArray(data)) return data as AvatarProfile[];
      return (Array.isArray(data?.profiles) ? data.profiles : (Array.isArray(data?.avatars) ? data.avatars : [])) as AvatarProfile[];
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const downloadSuperadminUsageCsv = useCallback(async (): Promise<boolean> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/superadmin/usage.csv`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) {
        let message = 'Failed to export superadmin usage';
        try {
          const data = await res.json();
          message = data.error || message;
        } catch {
          // CSV endpoint may not return JSON on server/proxy errors.
        }
        throw new Error(message);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'superadmin-usage.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  }, [authHeaders, logout]);

  const createUser = useCallback(async (payload: {
    username: string;
    password: string;
    email?: string | null;
    role: ManagedUserEditableRole;
    is_verified?: boolean;
  }): Promise<ManagedUser | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create user');
      return data.user || null;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const verifyUser = useCallback(async (userId: string): Promise<ManagedUser | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/verify`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to verify user');
      return data.user || null;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const resetUserPassword = useCallback(async (userId: string, password: string): Promise<ManagedUser | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/reset-password`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ password }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reset password');
      return data.user || null;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const updateUserRole = useCallback(async (
    userId: string,
    role: ManagedUserEditableRole,
  ): Promise<ManagedUser | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/role`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ role }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update role');
      return data.user || null;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const updateUserEmail = useCallback(async (
    userId: string,
    email: string | null,
  ): Promise<ManagedUser | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}/email`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ email }),
      });
      if (res.status === 401) { logout(); return null; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update email');
      return data.user || null;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const deleteUser = useCallback(async (userId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return false; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete user');
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const getFeedbackChat = useCallback(async (chatId: string): Promise<FeedbackChatData | null> => {
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${chatId}/feedback-chat`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) return null;
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  }, [authHeaders, logout]);

  const startFeedbackChat = useCallback(async (chatId: string): Promise<FeedbackChatData | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${chatId}/feedback-chat/start`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to start feedback chat');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const resetFeedbackChat = useCallback(async (chatId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${chatId}/feedback-chat`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.status === 401) { logout(); return false; }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to reset feedback chat');
      }
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const sendFeedbackChatMessage = useCallback(async (
    chatId: string,
    content: string,
    references: FeedbackReference[],
  ): Promise<FeedbackChatSendResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${chatId}/feedback-chat/message`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ content, references }),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send feedback chat message');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  const updateFeedbackModifier = useCallback(async (
    chatId: string,
    messageId: string,
    action: 'edit' | 'accept',
    modifierText?: string,
  ): Promise<FeedbackChatModifierUpdateResponse | null> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/conversations/${chatId}/feedback-chat/modifiers/${messageId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          action,
          ...(action === 'edit' ? { modifier_text: modifierText } : {}),
        }),
      });
      if (res.status === 401) { logout(); return null; }
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update modifier');
      }
      return await res.json();
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [authHeaders, logout]);

  return useMemo(() => ({
    isAuthenticated,
    username,
    adminSession,
    loading,
    error,
    login,
    logout,
    getAdminContext,
    switchAdminScope,
    getBehavior,
    getOnboardingMemory,
    getAdminProfile,
    saveBehavior,
    saveOnboardingMemory,
    saveAdminProfile,
    getUsers,
    getSuperadminOverview,
    getSuperadminUsers,
    getSuperadminAvatars,
    getSuperadminAvatarLab,
    getLiveAvatarCatalog,
    createAvatarProfile,
    updateAvatarProfile,
    setAvatarCustomers,
    getCustomerAvatars,
    downloadSuperadminUsageCsv,
    createUser,
    verifyUser,
    resetUserPassword,
    updateUserRole,
    updateUserEmail,
    deleteUser,
    getVocabulary,
    saveVocabulary,
    getPromptPreview,
    saveCorePrompt,
    getCallToActions,
    saveCallToActions,
    startAdminOnboarding,
    sendAdminOnboardingMessage,
    applyAdminOnboardingMemory,
    getKnowledgeSummary,
    saveKnowledgeSummary,
    generateKnowledgeSummary,
    getDocuments,
    getDocumentChunks,
    getChunkDetail,
    deleteChunk,
    deleteDocument,
    uploadDocument,
    getDeepScrapes,
    startDeepScrape,
    getDeepScrapeRun,
    cancelDeepScrape,
    activateDeepScrapeRun,
    getConversations,
    getDebugConversations,
    getDebugConversation,
    deleteDebugConversation,
    getDebugAudioUrl,
    getDebugProfiles,
    saveDebugProfile,
    cloneDebugProfile,
    deleteDebugProfile,
    getDebugEvaluations,
    saveDebugEvaluation,
    deleteConversation,
    loadFeedback,
    saveFeedback,
    generateFeedback,
    applyFeedback,
    getFeedbackChat,
    startFeedbackChat,
    resetFeedbackChat,
    sendFeedbackChatMessage,
    updateFeedbackModifier,
  }), [
    isAuthenticated,
    username,
    adminSession,
    loading,
    error,
    login,
    logout,
    getAdminContext,
    switchAdminScope,
    getBehavior,
    getOnboardingMemory,
    getAdminProfile,
    saveBehavior,
    saveOnboardingMemory,
    saveAdminProfile,
    getUsers,
    getSuperadminOverview,
    getSuperadminUsers,
    getSuperadminAvatars,
    getSuperadminAvatarLab,
    getLiveAvatarCatalog,
    createAvatarProfile,
    updateAvatarProfile,
    setAvatarCustomers,
    getCustomerAvatars,
    downloadSuperadminUsageCsv,
    createUser,
    verifyUser,
    resetUserPassword,
    updateUserRole,
    updateUserEmail,
    deleteUser,
    getVocabulary,
    saveVocabulary,
    getPromptPreview,
    saveCorePrompt,
    getCallToActions,
    saveCallToActions,
    startAdminOnboarding,
    sendAdminOnboardingMessage,
    applyAdminOnboardingMemory,
    getKnowledgeSummary,
    saveKnowledgeSummary,
    generateKnowledgeSummary,
    getDocuments,
    getDocumentChunks,
    getChunkDetail,
    deleteChunk,
    deleteDocument,
    uploadDocument,
    getDeepScrapes,
    startDeepScrape,
    getDeepScrapeRun,
    cancelDeepScrape,
    activateDeepScrapeRun,
    getConversations,
    getDebugConversations,
    getDebugConversation,
    deleteDebugConversation,
    getDebugAudioUrl,
    getDebugProfiles,
    saveDebugProfile,
    cloneDebugProfile,
    deleteDebugProfile,
    getDebugEvaluations,
    saveDebugEvaluation,
    deleteConversation,
    loadFeedback,
    saveFeedback,
    generateFeedback,
    applyFeedback,
    getFeedbackChat,
    startFeedbackChat,
    resetFeedbackChat,
    sendFeedbackChatMessage,
    updateFeedbackModifier,
  ]);
}

export type { VocabWord, BehaviorModifier, OnboardingMemoryData, AdminProfile, CustomerMembership, AdminSessionContext, ManagedUser, SuperadminUserAgentAssignment, SuperadminUserRecord, SuperadminUsersResponse, AvatarProfileStatus, AvatarAssignedCustomer, AvatarProfile, AvatarCustomerOption, SuperadminAvatarsResponse, AvatarLabSection, AvatarLabSnapshot, AvatarLabComparison, AvatarLabResponse, AvatarProfileInput, LiveAvatarCatalogSource, LiveAvatarCatalogEntry, LiveAvatarCatalogResponse, VocabularyData, PromptPreview, PromptSection, CallToActionsData, UsageBucket, UsagePricingConfig, WorkspaceOverview, CustomerOverview, SuperadminOverview, SuperadminBillingPeriod, SuperadminBillingSummary, SuperadminCostAlert, SuperadminReconciliation, SuperadminPricingCoverage, KnowledgeSummaryData, KnowledgeSummaryMetadata, DocumentInfo, ChunkInfo, ChunkDetail, DocumentListResponse, ChunkListResponse, DeepScrapeRunStatus, DeepScrapeConfig, DeepScrapeSource, DeepScrapeRun, DeepScrapeListResponse, DeepScrapeInput, ConversationSummary, DebugAudioMeta, DebugConversationMessage, DebugConversationSummary, DebugConversationDetail, DebugProfileSettings, DebugProfile, DebugProfilesResponse, DebugEvaluation, FeedbackComment, FeedbackData, FeedbackGenerateResponse, FeedbackApplyResponse, FeedbackReference, FeedbackChatModifier, FeedbackChatMessage, FeedbackChatData, FeedbackChatSendResponse, FeedbackChatModifierUpdateResponse, AdminOnboardingStartResponse, AdminOnboardingQuestionState, AdminOnboardingMessage, AdminOnboardingMessageResponse, AdminOnboardingMemoryApplyResponse };
